export class ApiError extends Error {
  constructor(
    readonly status: number,
    message: string,
    readonly code?: string,
    readonly body?: unknown,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export const sessionExpiredEvent = "budgeter:session-expired";

export interface ApiOptions {
  method?: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
  body?: unknown;
  form?: FormData;
  signal?: AbortSignal;
}

/** JSON API call. Mutations always send JSON or multipart (the server's CSRF rule). */
export async function api<T>(path: string, options: ApiOptions = {}): Promise<T> {
  const method = options.method ?? "GET";
  const headers: Record<string, string> = { accept: "application/json" };
  let body: BodyInit | undefined;
  if (options.form) {
    body = options.form;
  } else if (method !== "GET") {
    headers["content-type"] = "application/json";
    body = JSON.stringify(options.body ?? {});
  }
  let res: Response;
  try {
    res = await fetch(`/api/v1${path}`, {
      method,
      headers,
      body,
      credentials: "same-origin",
      signal: options.signal,
    });
  } catch {
    throw new ApiError(0, "You're offline. This needs a connection.");
  }
  if (res.status === 204) {
    return undefined as T;
  }
  const data: unknown = await res.json().catch(() => null);
  if (!res.ok) {
    const err = (data ?? {}) as { error?: string; code?: string };
    if (res.status === 401 && !path.startsWith("/auth/")) {
      window.dispatchEvent(new Event(sessionExpiredEvent));
    }
    throw new ApiError(res.status, err.error ?? `Request failed (${res.status})`, err.code, data);
  }
  return data as T;
}
